import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AuthState, UserProfile, LoginRequest, ApiResponse, LoginResponse } from '../models/types';
import { authService, getStoredToken, getStoredUser, clearStoredAuth } from '../services/api';

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<ApiResponse<LoginResponse>>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    loading: true,
  });

  // Verifica autenticazione all'avvio
  useEffect(() => {
    const initAuth = async () => {
      const token = getStoredToken();
      const user = getStoredUser();

      if (token && user) {
        // Verifica se il token � ancora valido
        const isValid = await authService.verifyToken();
        
        if (isValid) {
          setState({
            isAuthenticated: true,
            user,
            token,
            loading: false,
          });
        } else {
          // Token non valido, pulisci
          clearStoredAuth();
          setState({
            isAuthenticated: false,
            user: null,
            token: null,
            loading: false,
          });
        }
      } else {
        setState({
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false,
        });
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await authService.login(credentials);
      
      if (response.success && response.data) {
        setState({
          isAuthenticated: true,
          user: response.data.user,
          token: response.data.token,
          loading: false,
        });
      } else {
        setState(prev => ({ ...prev, loading: false }));
      }
      
      return response;
    } catch (error: any) {
      setState(prev => ({ ...prev, loading: false }));
      throw error;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      await authService.logout();
    } finally {
      setState({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      });
    }
  }, []);

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const response = await authService.getProfile();
      if (response.success && response.data) {
        setState(prev => ({
          ...prev,
          user: response.data!,
        }));
      }
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
